import { Component, effect, inject, OnDestroy, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent } from '@shared/forms';
import { QuizFacade } from '@app/store/quiz';
import { QuizQuestion } from '@app/store/quiz-questions/models/quiz-question';
import { QuestionSelectionService } from '@app/dialogs';
import { QuestionListComponent } from '../question-list/question-list.component';

@Component({
    selector: 'app-quiz-add-modal',
    templateUrl: './add.component.html',
    styleUrls: ['./add.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, FormInputComponent, QuestionListComponent],
})
export class AddComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly quizFacade = inject(QuizFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly questionSelectionService = inject(QuestionSelectionService);

    readonly saving = this.quizFacade.saving;

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
    });

    readonly draftQuestions = signal<QuizQuestion[]>([]);

    constructor() {
        effect(() => {
            const createdId = this.quizFacade.createdId();
            if (this.quizFacade.saved() && createdId) {
                this.router.navigate([{ outlets: { modal: ['quizzes', 'view', createdId] } }]);
            }
        });

        effect(() => {
            if (this.quizFacade.saving() === false && this.quizFacade.saved() === false) {
                this.form.enable();
            }
        });

        effect(() => {
            const question = this.questionSelectionService.selected();
            if (!question) return;

            this.addQuestion(question);
            this.questionSelectionService.clear();
        });
    }

    ngOnDestroy(): void {
        this.quizFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        if (this.form.valid) {
            this.form.disable();
            const questionIds = this.draftQuestions().map(({ id }) => id);
            this.quizFacade.createQuiz({ ...this.form.getRawValue(), questionIds });
        }
    }

    onAddQuestion(): void {
        this.router.navigate([{ outlets: { dialog: ['add-question'] } }]);
    }

    onRemoveQuestion(index: number): void {
        const current = this.draftQuestions();
        this.draftQuestions.set(current.filter((_, i) => i !== index));
    }

    reorderQuestions(questions: QuizQuestion[]): void {
        this.draftQuestions.set(questions);
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }

    private addQuestion(question: QuizQuestion): void {
        const current = this.draftQuestions();
        const alreadyExists = current.some(({ id }) => id === question.id);
        if (!alreadyExists) {
            this.draftQuestions.set([...current, question]);
        }
    }
}
